import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { confirmAction } from "../../utils/confirm";
import AppModal from "../../components/AppModal";
import Pagination from "../../components/Pagination/Pagination";
import FilterBar, { FilterField } from "../../components/FilterBar";
import { useListController } from "../../controllers/useListController";
import { rolesService } from "../../services/apiService";
import { usePermission } from "../../hooks/usePermission";
import { showToast } from "../../utils/toast";
import { validateForm } from "../../utils/validate";
import { ACTIVE_STATUS_FILTERS, emptyMessageForStatus, statusFromFilter } from "../../utils/listFilters";

const STATUS_FILTERS = ACTIVE_STATUS_FILTERS("All roles");

const EMPTY = { id: null, name: "", description: "", is_active: 1 };

export default function Roles() {
  const navigate = useNavigate();
  const { rows, pagination, loading, error, fetchRows } =
    useListController(rolesService);
  const { can } = usePermission();
  const canAdd = can("roles", "is_add");
  const canEdit = can("roles", "is_update");
  const canDelete = can("roles", "is_delete");

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [viewFilter, setViewFilter] = useState("all");
  const [search, setSearch] = useState("");

  const queryParams = (extra = {}) => ({
    page: extra.page ?? pagination.page,
    limit: extra.limit ?? pagination.limit,
    status: statusFromFilter(extra.filter ?? viewFilter, STATUS_FILTERS),
    search: extra.search ?? search,
  });

  useEffect(() => {
    fetchRows({ page: 1, status: statusFromFilter(viewFilter, STATUS_FILTERS), search: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewFilter]);

  const onSearch = (e) => {
    e.preventDefault();
    fetchRows({ page: 1, status: statusFromFilter(viewFilter, STATUS_FILTERS), search });
  };

  const onReset = (e) => {
    e.preventDefault();
    setViewFilter("all");
    setSearch("");
    fetchRows({ page: 1, status: "", search: "" });
  };

  const handleStatusChange = (e) => {
    setViewFilter(e.target.value);
    setSearch("");
  };

  const emptyMessage = emptyMessageForStatus(viewFilter, {
    all: "No roles yet",
    active: "No active roles",
    inactive: "No inactive roles",
  });

  const openAdd = () => {
    setForm(EMPTY);
    setModalOpen(true);
  };
  const openEdit = (row) => {
    setForm({ ...EMPTY, ...row });
    setModalOpen(true);
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? (checked ? 1 : 0) : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm(e.currentTarget, [{ name: "name", value: form.name, label: "Name", required: true }]))
      return;
    setSubmitting(true);
    try {
      const res = form.id
        ? await rolesService.update(form)
        : await rolesService.create(form);
      if (res?.success) {
        showToast("success", form.id ? "Role updated" : "Role created");
        setModalOpen(false);
        fetchRows(queryParams());
      } else showToast("error", res?.msg || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (row) => {
    const r = await confirmAction({
      title: `Delete role "${row.name}"?`,
      icon: "warning",
      confirmText: "Delete",
      variant: "danger",
    });
    if (!r.isConfirmed) return;
    const res = await rolesService.delete(row.id);
    if (res?.success) {
      showToast("success", "Role deleted");
      fetchRows(queryParams());
    } else showToast("error", res?.msg || "Failed");
  };

  return (
    <div className="card mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <strong>Roles</strong>
        {canAdd && (
          <button className="btn btn-primary" onClick={openAdd}>
            <i className="cil-plus me-1" /> Add Role
          </button>
        )}
      </div>
      <div className="card-body">
        {error && <div className="alert alert-danger">{error}</div>}

        <FilterBar onSubmit={onSearch} onReset={onReset}>
          <FilterField id="role-status-filter" label="Show">
            <select
              id="role-status-filter"
              className="form-select"
              value={viewFilter}
              onChange={handleStatusChange}
            >
              {STATUS_FILTERS.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          </FilterField>
          <FilterField id="role-search" label="Search">
            <input
              id="role-search"
              className="form-control"
              placeholder="Role name or description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </FilterField>
        </FilterBar>

        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Role</th>
                <th>Description</th>
                <th>Status</th>
                <th style={{ width: 240 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={4} className="text-center py-4">Loading…</td></tr>)}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={4} className="text-center py-4 text-muted">{emptyMessage}</td></tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr key={r.id}>
                    <td className="fw-semibold">
                      {r.name === "group_admin" ? "Group Admin" : r.name === "centre_admin" ? "Centre Admin" : r.name}
                    </td>
                    <td className="small text-muted">{r.description || "—"}</td>
                    <td>
                      {r.is_active ? (
                        <span className="badge bg-success">Active</span>
                      ) : (
                        <span className="badge bg-secondary">Inactive</span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        {canEdit && (
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate(`/roles/${r.id}/permissions`)}>
                            Permissions
                          </button>
                        )}
                        {canEdit && (
                          <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(r)}>Edit</button>
                        )}
                        {canDelete && (
                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(r)}>Delete</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <Pagination
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.total}
          itemsCount={rows.length}
          loading={loading}
          onPageChange={(page) => fetchRows({ ...queryParams(), page })}
          onLimitChange={(limit) => fetchRows({ ...queryParams(), page: 1, limit })}
        />
      </div>

      <AppModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={form.id ? "Edit Role" : "Add Role"}
        footer={
          <>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" form="role-form" className="btn btn-primary" disabled={submitting}>
              {submitting ? "Saving…" : form.id ? "Save Changes" : "Create"}
            </button>
          </>
        }
      >
              <form id="role-form" onSubmit={handleSubmit} noValidate>
                <div className="mb-3">
                  <label className="form-label">Name <span className="text-danger">*</span></label>
                  <input name="name" className="form-control" value={form.name} onChange={onChange} required />
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea name="description" className="form-control" rows={2} value={form.description || ""} onChange={onChange} />
                </div>
                <div className="form-check">
                  <input type="checkbox" id="roleActive" className="form-check-input" name="is_active" checked={!!form.is_active} onChange={onChange} />
                  <label htmlFor="roleActive" className="form-check-label ms-1">Active</label>
                </div>
              </form>
      </AppModal>
    </div>
  );
}
